import "./styles.css";
import { activeCourse as course } from "./data/course";
import type { Lesson, Module } from "./types";

const root = document.querySelector<HTMLDivElement>("#app");
if (!root) throw new Error("App root is missing");
const app: HTMLDivElement = root;

const storageKey = `jmpanal-course:${course.id}:completed`;
const number = new Intl.NumberFormat("en-US", { minimumIntegerDigits: 2 });
type LessonRail = "left" | "right";

function lessonRailOpen(side: LessonRail): boolean {
  try {
    return localStorage.getItem(`jmpanal-course:${course.id}:lesson-rail:${side}`) === "open";
  } catch {
    return false;
  }
}

function saveLessonRail(side: LessonRail, open: boolean): void {
  try {
    localStorage.setItem(`jmpanal-course:${course.id}:lesson-rail:${side}`, open ? "open" : "closed");
  } catch {
    // Layout still works when storage is unavailable.
  }
}

function readProgress(): Set<string> {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey) ?? "[]");
    return new Set(Array.isArray(value) ? value.filter((item) => typeof item === "string") : []);
  } catch {
    return new Set();
  }
}

let completed = readProgress();

const lessonHref = (lesson: Lesson): string =>
  lesson.number === 0 ? "/setup" : `/lesson/${lesson.slug}`;

const moduleLessons = (module: Module): Lesson[] =>
  course.lessons.filter(({ moduleId }) => moduleId === module.id);

const progressPercent = (): number =>
  Math.round((completed.size / course.lessons.length) * 100);

const totalHours = (): string => `${Math.round(course.totalMinutes / 60)} hours`;

const escapeText = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

function highlightCode(source: string): string {
  const tokenPattern = /("""[\s\S]*?"""|'''[\s\S]*?'''|`(?:\\.|[^`\\])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')|(#.*$|\/\/.*$)|\b(class|def|return|from|import|as|async|await|for|while|if|elif|else|try|except|finally|with|yield|lambda|raise|pass|break|continue|in|is|not|and|or|match|case|function|const|let|var|new|throw|catch|switch|export|default|interface|type|extends|implements|public|private|protected|static|SELECT|FROM|WHERE|INSERT|UPDATE|DELETE|CREATE|TABLE|JOIN|ON|GROUP|ORDER|BY|LIMIT|VALUES|SET|INTO|AS)\b|\b(True|False|None|null|undefined|true|false)\b|\b(\d+(?:\.\d+)?)\b|\b([A-Z][A-Za-z0-9_]*)\b|\b([A-Za-z_][A-Za-z0-9_]*)(?=\s*\()|([=+\-*\/%<>!&|:]+)/gm;
  let html = "";
  let lastIndex = 0;

  for (const match of source.matchAll(tokenPattern)) {
    const index = match.index ?? 0;
    html += escapeText(source.slice(lastIndex, index));
    const value = escapeText(match[0]);
    const className = match[1]
      ? "tok-string"
      : match[2]
        ? "tok-comment"
        : match[3]
          ? "tok-keyword"
          : match[4]
            ? "tok-constant"
            : match[5]
              ? "tok-number"
              : match[6]
                ? "tok-type"
                : match[7]
                  ? "tok-function"
                  : "tok-operator";
    html += `<span class="${className}">${value}</span>`;
    lastIndex = index + match[0].length;
  }

  return html + escapeText(source.slice(lastIndex));
}

function logo(): string {
  return `
    <a class="brand" href="/" aria-label="${course.title} home">
      <span class="brand-mark" aria-hidden="true">
        <span></span><span></span><span></span>
      </span>
      <span class="brand-type"><strong>${course.shortTitle}</strong><small>${course.id === "agents" ? "AI systems course" : "distributed systems course"}</small></span>
    </a>`;
}

type IconName = "arrow-left" | "arrow-right" | "book" | "check" | "clock" | "flag" | "home" | "menu" | "panel-left" | "panel-right" | "progress" | "search" | "terminal";

function icon(name: IconName): string {
  const paths: Record<IconName, string> = {
    "arrow-left": '<path d="m15 18-6-6 6-6"/><path d="M9 12h11"/>',
    "arrow-right": '<path d="m9 18 6-6-6-6"/><path d="M4 12h11"/>',
    book: '<path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H11v17H7.5A3.5 3.5 0 0 0 4 22Z"/><path d="M20 5.5A3.5 3.5 0 0 0 16.5 2H13v17h3.5A3.5 3.5 0 0 1 20 22Z"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
    flag: '<path d="M5 21V4"/><path d="M5 5h11l-2 4 2 4H5"/>',
    home: '<path d="m3 11 9-8 9 8"/><path d="M5 10v10h14V10"/><path d="M10 20v-6h4v6"/>',
    menu: '<path d="M4 7h16M4 12h16M4 17h16"/>',
    "panel-left": '<rect x="3" y="4" width="18" height="16" rx="1"/><path d="M9 4v16"/><path d="m15 9-3 3 3 3"/>',
    "panel-right": '<rect x="3" y="4" width="18" height="16" rx="1"/><path d="M15 4v16"/><path d="m9 9 3 3-3 3"/>',
    progress: '<path d="M4 19V9M10 19V5M16 19v-7M22 19V2"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/>',
    terminal: '<path d="m5 7 4 4-4 4"/><path d="M12 17h7"/><rect x="2" y="3" width="20" height="18" rx="1"/>',
  };
  return `<svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true">${paths[name]}</svg>`;
}

function header(active = ""): string {
  return `
    <header class="site-header">
      <div class="header-inner">
        ${logo()}
        <nav class="primary-nav" aria-label="Primary navigation">
          <a href="/" ${active === "home" ? 'aria-current="page"' : ""}>${icon("home")}Course home</a>
          <a href="/curriculum" ${active === "curriculum" ? 'aria-current="page"' : ""}>${icon("book")}Curriculum</a>
        </nav>
        <div class="header-actions">
          <button class="search-trigger" type="button" data-open-search aria-label="Search lessons">
            ${icon("search")}
            <span>Search</span><kbd>/</kbd>
          </button>
          <a class="progress-pill" href="/#course-progress" aria-label="${completed.size} of ${course.lessons.length} lessons complete">
            ${icon("progress")}<span>Progress ${progressPercent()}%</span>
            <i style="--progress:${progressPercent()}%"></i>
          </a>
        </div>
        <button class="menu-trigger" type="button" aria-label="Open navigation" aria-expanded="false" data-menu-trigger>
          <span></span><span></span>
        </button>
      </div>
      <nav class="mobile-nav" aria-label="Mobile navigation" data-mobile-nav hidden>
        <a href="/">Course home</a><a href="/curriculum">Curriculum</a><a href="/setup">Environment setup</a>
        <button type="button" data-open-search>Search lessons</button>
      </nav>
    </header>`;
}

function footer(): string {
  return `
    <footer class="site-footer">
      <div class="footer-grid">
        <div>${logo()}<p>${course.accentLabel}</p></div>
        <div><p class="footer-label">Course</p><a href="/curriculum">Curriculum</a><a href="/setup">Environment setup</a><a href="/learn">Your progress</a></div>
        <div><p class="footer-label">Learning model</p><span>Self-paced</span><span>Progress stays in this browser</span><span>All lessons open</span></div>
      </div>
      <div class="footer-base"><span>JMP Anal Course Studio</span><span>Built for engineers who ship.</span></div>
    </footer>`;
}

function searchDialog(): string {
  return `
    <dialog class="search-dialog" data-search-dialog>
      <div class="search-head">
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><path d="m20 20-4-4"></path></svg>
        <label class="sr-only" for="global-search">Search lessons</label>
        <input id="global-search" type="search" placeholder="Search concepts or lesson titles" autocomplete="off" data-global-search />
        <button type="button" data-close-search aria-label="Close search">Esc</button>
      </div>
      <div class="search-results" data-search-results aria-live="polite">
        <p class="search-hint">Try idempotency, memory, queues, or SLO.</p>
      </div>
    </dialog>`;
}

function courseOrientationVisual(): string {
  return `<figure class="course-orientation">
    <picture>
      <source srcset="/images/systems-flow-91be2f912563.avif" type="image/avif" />
      <img src="/images/systems-flow-1239ccd7667d.webp" width="1536" height="1024" alt="Documentary close view of fiber patch panels and worn cable guides in a working data center." />
    </picture>
    <figcaption><strong>Every request reaches physical infrastructure.</strong><span>Patch panels and cable paths make each network hop concrete before software adds queues, retries, and replicated state.</span></figcaption>
  </figure>`;
}

function lessonVisual(lesson: Lesson): string {
  if (course.id !== "systems" || lesson.number !== 35) return "";
  return `<figure class="lesson-editorial-visual">
    <picture>
      <source srcset="/images/durable-pipeline-7a3885a9d39b.avif" type="image/avif" />
      <img src="/images/durable-pipeline-d72d7d0be070.webp" width="1672" height="941" alt="Documentary close view of redundant storage and server hardware with small green and amber status lights." />
    </picture>
    <figcaption><strong>Durability rests on redundant physical systems.</strong><span>Software recovery still depends on hardware paths that preserve state when one component stops serving.</span></figcaption>
  </figure>`;
}

function shell(content: string, active: string, className = ""): string {
  return `${header(active)}<main id="main" class="${className}">${content}</main>${footer()}${searchDialog()}`;
}

function dashboardLessonRow(lesson: Lesson, current: Lesson): string {
  const done = completed.has(lesson.slug);
  const isCurrent = lesson.slug === current.slug;
  return `
    <a class="dashboard-lesson-row ${done ? "is-complete" : ""} ${isCurrent ? "is-current" : ""}" href="${lessonHref(lesson)}" ${isCurrent ? 'aria-current="step"' : ""}>
      <span class="dashboard-lesson-state" aria-label="${done ? "Complete" : isCurrent ? "Current lesson" : "Not complete"}">${done ? icon("check") : `<i></i>`}</span>
      <span class="dashboard-lesson-number">${number.format(lesson.number)}</span>
      <strong>${lesson.title}</strong>
      <small>${lesson.minutes} min</small>
      ${icon("arrow-right")}
    </a>`;
}

function courseModulePanel(module: Module, current: Lesson, expanded: boolean): string {
  const lessons = moduleLessons(module);
  const done = lessons.filter((lesson) => completed.has(lesson.slug)).length;
  return `
    <details class="course-module-panel" ${expanded ? "open" : ""} data-searchable="${escapeText(`${module.title} ${module.description} ${lessons.map((lesson) => `${lesson.title} ${lesson.summary}`).join(" ")}`.toLowerCase())}">
      <summary>
        <span>Module ${module.number} ·</span>
        <strong>${module.title}</strong>
        <small>${done}/${lessons.length} complete</small>
        <span class="module-chevron" aria-hidden="true"></span>
      </summary>
      <p class="course-module-description">${module.description}</p>
      <div class="dashboard-lesson-list">${lessons.map((lesson) => dashboardLessonRow(lesson, current)).join("")}</div>
      <div class="course-module-milestone"><span>Module deliverable</span><strong>${module.deliverable}</strong></div>
    </details>`;
}

function courseToolLinks(active: "home" | "curriculum" = "home", compact = false): string {
  return `
    <nav class="course-tool-links ${compact ? "is-compact" : ""}" aria-label="Course tools">
      <a href="/setup">${icon("terminal")}<span><strong>${compact ? "Setup" : "Environment setup"}</strong><small>Prepare the workspace</small></span>${icon("arrow-right")}</a>
      <a href="/curriculum" ${active === "curriculum" ? 'aria-current="page"' : ""}>${icon("book")}<span><strong>Curriculum</strong><small>Browse every module</small></span>${icon("arrow-right")}</a>
      <a href="/#capstone">${icon("flag")}<span><strong>${compact ? "Capstone" : "Capstone project"}</strong><small>Review final deliverables</small></span>${icon("arrow-right")}</a>
    </nav>`;
}

function keyboardTip(): string {
  return `<section class="course-keyboard-tip"><span>Keyboard</span><p><kbd>↑</kbd><kbd>↓</kbd> Move between lessons</p><p><kbd>Enter</kbd> Open the selected lesson</p></section>`;
}

function courseNextRail(current: Lesson, currentModule: Module, afterCurrent: Lesson, view: "home" | "curriculum"): string {
  if (course.id === "systems") {
    return `<aside class="course-next-rail systems-module-rail" aria-label="Current module">
      <section class="systems-next-lesson"><span>Next up</span><strong>Lesson ${number.format(current.number)}</strong><h2><a href="${lessonHref(current)}">${current.title}</a></h2><p>~${current.minutes} min</p></section>
      <section class="systems-module-lessons"><span>In this module</span><ol>${moduleLessons(currentModule).map((lesson) => `<li class="${lesson.slug === current.slug ? "is-current" : ""}"><a href="${lessonHref(lesson)}"><span>${number.format(lesson.number)}</span><strong>${lesson.title}</strong></a></li>`).join("")}</ol></section>
      ${keyboardTip()}
    </aside>`;
  }
  return `<aside class="course-next-rail" aria-label="Next steps">
    <section><span>Up next</span><h2>${afterCurrent.title}</h2><p>${afterCurrent.summary}</p><a href="${lessonHref(afterCurrent)}">View lesson ${number.format(afterCurrent.number)} ${icon("arrow-right")}</a></section>
    <section id="course-progress"><span>Progress</span><p><strong>${completed.size}</strong> of ${course.lessons.length} complete</p><div class="course-progress-track"><i style="width:${progressPercent()}%"></i></div><small>${progressPercent()}% complete</small></section>
    <section><span>Course tools</span>${courseToolLinks(view)}</section>
    ${keyboardTip()}
  </aside>`;
}

function renderCourseWorkspace(view: "home" | "curriculum"): string {
  const current = getContinueLesson();
  const currentModule = course.modules.find(({ id }) => id === current.moduleId) ?? course.modules[0];
  if (!currentModule) throw new Error("Course has no modules");
  const currentIndex = course.lessons.findIndex(({ slug }) => slug === current.slug);
  const afterCurrent = course.lessons[Math.min(currentIndex + 1, course.lessons.length - 1)] ?? current;
  const isCurriculum = view === "curriculum";
  const mainTitle = isCurriculum ? "Course curriculum" : "Continue learning";

  return `${header(view)}
    <main id="main" class="course-workspace ${isCurriculum ? "is-curriculum" : ""}">
      <aside class="course-rail" aria-label="Course overview">
        <div class="course-rail-title"><strong>${course.title}</strong><span>${course.modules.length} modules · ${course.lessons.length} lessons</span></div>
        <section aria-labelledby="rail-progress-title">
          <h2 id="rail-progress-title">Course progress</h2>
          <p><strong>${completed.size}</strong> of ${course.lessons.length} complete</p>
          <div class="course-progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="${course.lessons.length}" aria-valuenow="${completed.size}"><i style="width:${progressPercent()}%"></i></div>
        </section>
        <h2>Course tools</h2>
        ${courseToolLinks(view, true)}
      </aside>

      <section class="course-path" aria-labelledby="course-path-title">
        <header class="course-path-header">
          <p>${course.title}</p>
          <h1 id="course-path-title">${mainTitle}</h1>
          ${isCurriculum ? `<label class="dashboard-filter">${icon("search")}<span class="sr-only">Filter curriculum</span><input type="search" placeholder="Filter modules and lessons" data-dashboard-filter></label>` : ""}
        </header>
        ${isCurriculum || course.id !== "systems" ? "" : courseOrientationVisual()}
        ${isCurriculum ? "" : `
          <section class="course-resume" aria-label="Current lesson">
            <span class="resume-play" aria-hidden="true"></span><div><span>Lesson ${number.format(current.number)}</span><h2>${current.title}</h2><p>Module ${currentModule.number} · ${currentModule.title}</p></div>
            <div><a class="button button-primary" href="${lessonHref(current)}">${completed.size ? "Continue lesson" : "Start lesson"}${icon("arrow-right")}</a><a href="/setup">Environment setup ${icon("arrow-right")}</a></div>
          </section>`}
        <div class="course-module-list" data-dashboard-list>
          ${course.modules.map((module) => courseModulePanel(module, current, module.id === currentModule.id)).join("")}
        </div>
        <section class="course-capstone-panel" id="capstone" aria-labelledby="capstone-title">
          <div><span>Capstone project</span><h2 id="capstone-title">${course.project.title}</h2><p>${course.project.brief}</p></div>
          <ol>${course.project.deliverables.map((item) => `<li>${item}</li>`).join("")}</ol>
        </section>
      </section>

      ${courseNextRail(current, currentModule, afterCurrent, view)}
    </main>${searchDialog()}`;
}

function renderHome(): string {
  return renderCourseWorkspace("home");
}

function moduleCard(module: Module): string {
  const lessons = moduleLessons(module);
  return `
    <article class="module-card">
      <div class="module-card-top"><span>M${number.format(module.number)}</span><span>${lessons.length} lessons</span></div>
      <p>${module.eyebrow}</p><h3>${module.title}</h3><div class="module-line"></div>
      <p class="module-description">${module.description}</p>
      <div class="module-milestone"><small>Milestone</small><strong>${module.milestone}</strong></div>
      <a href="${lessonHref(lessons[0]!)}" aria-label="Start ${module.title}">Start module</a>
    </article>`;
}

function lessonRow(lesson: Lesson): string {
  const done = completed.has(lesson.slug);
  return `
    <a class="lesson-row ${done ? "is-complete" : ""}" href="${lessonHref(lesson)}">
      <span class="lesson-status" aria-label="${done ? "Complete" : "Not complete"}">${done ? "✓" : number.format(lesson.number)}</span>
      <span class="lesson-row-copy"><strong>${lesson.title}</strong><small>${lesson.summary}</small></span>
      <span class="lesson-time">${lesson.minutes} min</span><span class="row-arrow" aria-hidden="true">↗</span>
    </a>`;
}

function renderCurriculum(): string {
  return renderCourseWorkspace("curriculum");
}

function syllabusModule(module: Module): string {
  const lessons = moduleLessons(module);
  const minutes = lessons.reduce((sum, lesson) => sum + lesson.minutes, 0);
  return `
    <section class="syllabus-module" data-searchable="${escapeText(`${module.title} ${module.description} ${lessons.map((lesson) => `${lesson.title} ${lesson.summary}`).join(" ")}`.toLowerCase())}">
      <div class="module-index"><span>${number.format(module.number)}</span><i></i></div>
      <div class="module-body">
        <header><div><p>${module.eyebrow}</p><h3>${module.title}</h3></div><div><span>${lessons.length} lessons</span><span>${minutes} min</span></div></header>
        <p class="module-lede">${module.description}</p>
        <div class="lesson-rows">${lessons.map(lessonRow).join("")}</div>
        <div class="module-deliverable"><small>Module deliverable</small><strong>${module.deliverable}</strong></div>
      </div>
    </section>`;
}

function getContinueLesson(): Lesson {
  return course.lessons.find((lesson) => !completed.has(lesson.slug)) ?? course.lessons.at(-1)!;
}

function renderLearn(): string {
  return renderHome();
}

function studyModule(module: Module): string {
  const lessons = moduleLessons(module);
  const done = lessons.filter((lesson) => completed.has(lesson.slug)).length;
  return `
    <article class="study-module ${done === lessons.length ? "is-complete" : ""}">
      <header><span>M${number.format(module.number)}</span><div><p>${module.eyebrow}</p><h3>${module.title}</h3></div><strong>${done}/${lessons.length}</strong></header>
      <div class="module-progress"><i style="width:${Math.round((done / lessons.length) * 100)}%"></i></div>
      <div class="study-lessons">${lessons.map((lesson) => `<a href="${lessonHref(lesson)}"><span>${completed.has(lesson.slug) ? "✓" : number.format(lesson.number)}</span><strong>${lesson.title}</strong><small>${lesson.minutes} min</small></a>`).join("")}</div>
    </article>`;
}

function sidebarLesson(lesson: Lesson, current: Lesson): string {
  const done = completed.has(lesson.slug);
  const isCurrent = lesson.slug === current.slug;
  return `<a href="${lessonHref(lesson)}" class="${isCurrent ? "is-current" : ""} ${done ? "is-complete" : ""}" ${isCurrent ? 'aria-current="page"' : ""}><span>${done ? icon("check") : number.format(lesson.number)}</span><strong>${lesson.title}</strong><small>${lesson.minutes} min</small></a>`;
}

function renderLesson(lesson: Lesson): string {
  const module = lesson.number === 0 ? course.modules[0] : course.modules.find(({ id }) => id === lesson.moduleId);
  if (!module) throw new Error(`Module missing for ${lesson.slug}`);
  const all = [course.setup, ...course.lessons];
  const index = all.findIndex(({ slug }) => slug === lesson.slug);
  const previous = index > 0 ? all[index - 1] : undefined;
  const next = index < all.length - 1 ? all[index + 1] : undefined;
  const activeLessons = lesson.number === 0 ? [course.setup, ...moduleLessons(module)] : moduleLessons(module);
  const moduleDone = moduleLessons(module).filter((entry) => completed.has(entry.slug)).length;
  const moduleLabel = lesson.number === 0 ? "Course setup" : `Module ${number.format(module.number)} · ${module.title}`;
  const leftRailOpen = lessonRailOpen("left");
  const rightRailOpen = lessonRailOpen("right");
  document.title = `${lesson.title} | ${course.title}`;

  return `<header class="lesson-app-header">
      ${logo()}
      <button class="lesson-mobile-menu" type="button" data-open-sidebar aria-controls="lesson-sidebar" aria-expanded="false" aria-label="Open module navigation">${icon("menu")}<span>Module</span></button>
      <p><span>${moduleLabel}</span><i>/</i><strong>${lesson.number === 0 ? "Setup" : `Lesson ${number.format(lesson.number)}`}</strong></p>
      ${course.id === "agents" ? `<nav class="agent-header-tools" aria-label="Lesson tools"><a href="/curriculum">${icon("book")}Docs</a><span aria-hidden="true">☀</span><b aria-label="Learner profile">AG</b></nav>` : lesson.number > 0 ? `<button type="button" class="complete-button lesson-complete-top ${completed.has(lesson.slug) ? "is-complete" : ""}" data-toggle-complete="${lesson.slug}" aria-pressed="${completed.has(lesson.slug)}">${icon("check")}${completed.has(lesson.slug) ? "Completed" : "Mark complete"}</button>` : `<a class="lesson-home-link" href="/">Course home</a>`}
    </header>
    <main id="main" class="lesson-page ${leftRailOpen ? "" : "is-left-collapsed"} ${rightRailOpen ? "" : "is-right-collapsed"}">
      <button class="lesson-rail-toggle lesson-rail-toggle-left" type="button" data-toggle-lesson-rail="left" aria-controls="lesson-sidebar" aria-expanded="${leftRailOpen}" aria-label="${leftRailOpen ? "Hide" : "Show"} curriculum" title="${leftRailOpen ? "Hide" : "Show"} curriculum">${icon("panel-left")}</button>
      <button class="lesson-rail-toggle lesson-rail-toggle-right" type="button" data-toggle-lesson-rail="right" aria-controls="lesson-outline" aria-expanded="${rightRailOpen}" aria-label="${rightRailOpen ? "Hide" : "Show"} lesson outline" title="${rightRailOpen ? "Hide" : "Show"} lesson outline">${icon("panel-right")}</button>
      <aside id="lesson-sidebar" class="lesson-sidebar" aria-label="Course lessons">
        <div class="sidebar-head"><a href="/">${icon("arrow-left")}Course home</a><button type="button" data-close-sidebar aria-label="Close lesson navigation">×</button></div>
        <div class="lesson-module-selector">
          <label for="lesson-module-select">${lesson.number === 0 ? "Course setup" : `Module ${module.number}`}</label>
          <strong class="lesson-module-title">${lesson.number === 0 ? "Environment setup" : module.title}</strong>
          <select id="lesson-module-select" data-module-select>
            <option value="/setup" ${lesson.number === 0 ? "selected" : ""}>Course setup</option>
            ${course.modules.map((item) => `<option value="${lessonHref(moduleLessons(item)[0]!)}" ${item.id === module.id && lesson.number > 0 ? "selected" : ""}>M${number.format(item.number)} · ${item.title}</option>`).join("")}
          </select>
          <p>${module.description}</p>
        </div>
        <div class="sidebar-module-progress"><span>${moduleDone}/${moduleLessons(module).length} lessons</span><i><b style="width:${Math.round((moduleDone / moduleLessons(module).length) * 100)}%"></b></i></div>
        <nav class="sidebar-modules current-module-only" data-sidebar-list aria-label="${module.title} lessons">
          <div class="sidebar-module" data-searchable="${escapeText(`${module.title} ${activeLessons.map((entry) => entry.title).join(" ")}`.toLowerCase())}">${activeLessons.map((entry) => sidebarLesson(entry, lesson)).join("")}</div>
        </nav>
        <a class="sidebar-course-map" href="/curriculum">${icon("book")}<span>Full curriculum</span>${icon("arrow-right")}</a>
      </aside>
      <div class="lesson-reader">
        <article class="lesson-article">
          <header class="lesson-title-block">
            <p class="lesson-breadcrumb">${moduleLabel} <span>/</span> ${lesson.number === 0 ? "Setup" : `Lesson ${number.format(lesson.number)}`}</p>
            <h1>${lesson.title}</h1>
            <p>${lesson.summary}</p>
            <div class="lesson-actions">
              <span>${icon("clock")}${lesson.minutes} minutes</span><span>${icon("terminal")}Runnable lab</span>
            </div>
            ${course.id === "agents" && lesson.number > 0 ? `<button type="button" class="complete-button lesson-complete-inline ${completed.has(lesson.slug) ? "is-complete" : ""}" data-toggle-complete="${lesson.slug}" aria-pressed="${completed.has(lesson.slug)}">${icon("check")}${completed.has(lesson.slug) ? "Completed" : "Mark complete"}</button>` : ""}
          </header>
          ${lessonVisual(lesson)}
          <div class="lesson-objective"><span>Objective</span><p>${lesson.summary}</p></div>
          <div class="lesson-content" data-lesson-content>${lesson.body}</div>
          <div class="lesson-completion-bar">
            ${lesson.number > 0 ? `<button type="button" class="complete-button ${completed.has(lesson.slug) ? "is-complete" : ""}" data-toggle-complete="${lesson.slug}" aria-pressed="${completed.has(lesson.slug)}">${icon("check")}${completed.has(lesson.slug) ? "Lesson completed" : "Mark lesson complete"}</button>` : `<span>Setup guide</span>`}
            <small>${completed.has(lesson.slug) ? "Progress saved in this browser." : "Complete the lesson when you can explain and run it."}</small>
          </div>
          <nav class="lesson-nav" aria-label="Lesson navigation">
            ${previous ? `<a href="${lessonHref(previous)}">${icon("arrow-left")}<span>Previous<strong>${previous.title}</strong></span></a>` : "<span></span>"}
            ${next ? `<a href="${lessonHref(next)}"><span>Next<strong>${next.title}</strong></span>${icon("arrow-right")}</a>` : `<a href="/"><span>Course complete<strong>Review course home</strong></span>${icon("arrow-right")}</a>`}
          </nav>
        </article>
      </div>
      <aside id="lesson-outline" class="lesson-outline" aria-label="Lesson outline"><p>Lesson outline</p><nav data-outline></nav><div><span>${completed.has(lesson.slug) ? "Lesson complete" : "In progress"}</span><a href="/">Course home ${icon("arrow-right")}</a></div></aside>
    </main>${searchDialog()}`;
}

function renderNotFound(): string {
  return shell(`<section class="not-found section-pad"><p class="eyebrow">404 / Off the map</p><h1>This lesson does not exist.</h1><p>Return to the curriculum and pick up the thread.</p><a class="button button-primary" href="/curriculum">Open curriculum</a></section>`, "", "not-found-page");
}

function route(): void {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  document.documentElement.dataset.course = course.id;
  document.title = course.title;
  document.querySelector('meta[name="description"]')?.setAttribute("content", course.description);
  if (path === "/") app.innerHTML = renderHome();
  else if (path === "/curriculum") app.innerHTML = renderCurriculum();
  else if (path === "/learn") app.innerHTML = renderLearn();
  else if (path === "/setup") app.innerHTML = renderLesson(course.setup);
  else if (path.startsWith("/lesson/")) {
    const slug = path.slice("/lesson/".length);
    const lesson = course.lessons.find((item) => item.slug === slug);
    app.innerHTML = lesson ? renderLesson(lesson) : renderNotFound();
  } else app.innerHTML = renderNotFound();
  enhance();
  window.scrollTo({ top: 0, behavior: "instant" });
  if (window.location.hash) {
    window.requestAnimationFrame(() => document.querySelector(window.location.hash)?.scrollIntoView());
  }
}

function navigate(href: string): void {
  history.pushState({}, "", href);
  route();
}

function searchResults(query: string): string {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return '<p class="search-hint">Try idempotency, memory, queues, or SLO.</p>';
  const results = course.lessons
    .filter((lesson) => `${lesson.title} ${lesson.summary} ${lesson.body.replace(/<[^>]+>/g, " ")}`.toLowerCase().includes(normalized))
    .slice(0, 10);
  if (!results.length) return `<p class="search-hint">No lesson matches <strong>${escapeText(query)}</strong>. Try a broader concept.</p>`;
  return results.map((lesson) => `<a href="${lessonHref(lesson)}"><span>${number.format(lesson.number)}</span><div><strong>${lesson.title}</strong><small>${lesson.summary}</small></div><i>↗</i></a>`).join("");
}

function codeLanguage(caption: string): string | undefined {
  const normalized = caption.trim().toLowerCase();
  if (/^(python|node|terminal|console|output|captured|expected|result|what you get)\b/.test(normalized)) return undefined;
  if (/\.py\b/.test(normalized)) return "python";
  if (/\.(?:js|mjs|cjs|ts)\b/.test(normalized)) return "javascript";
  if (/\.sql\b|^sql\b/.test(normalized)) return "sql";
  if (/\.json\b/.test(normalized)) return "json";
  return undefined;
}

function openSearch(): void {
  const dialog = document.querySelector<HTMLDialogElement>("[data-search-dialog]");
  if (!dialog) return;
  dialog.showModal();
  const input = dialog.querySelector<HTMLInputElement>("[data-global-search]");
  input?.focus();
}

function enhanceLesson(): void {
  const content = document.querySelector<HTMLElement>("[data-lesson-content]");
  if (!content) return;
  content.querySelectorAll("figure").forEach((figure) => {
    const pre = figure.querySelector("pre");
    if (!pre || figure.querySelector(".copy-code")) return;
    const code = pre.querySelector("code");
    const caption = figure.querySelector("figcaption")?.textContent ?? "";
    const language = codeLanguage(caption);
    figure.classList.add(language ? "code-figure" : "output-figure");
    if (code && !code.dataset.highlighted) {
      if (language) code.innerHTML = highlightCode(code.textContent ?? "");
      code.classList.add(`language-${language ?? "output"}`);
      code.dataset.highlighted = "true";
    }
    const button = document.createElement("button");
    button.type = "button";
    button.className = "copy-code";
    button.textContent = language ? "Copy code" : "Copy output";
    button.setAttribute("aria-label", `${language ? "Copy code" : "Copy output"} to clipboard`);
    button.addEventListener("click", async () => {
      const value = pre.textContent ?? "";
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        const fallback = document.createElement("textarea");
        fallback.value = value;
        fallback.setAttribute("readonly", "");
        fallback.style.position = "fixed";
        fallback.style.opacity = "0";
        document.body.append(fallback);
        fallback.select();
        document.execCommand("copy");
        fallback.remove();
      }
      button.textContent = "Copied";
      window.setTimeout(() => (button.textContent = language ? "Copy code" : "Copy output"), 1400);
    });
    figure.append(button);
  });
  const outline = document.querySelector<HTMLElement>("[data-outline]");
  if (!outline) return;
  const headings = [...content.querySelectorAll<HTMLHeadingElement>("h2")];
  headings.forEach((heading, index) => {
    heading.id = `section-${index + 1}`;
    const link = document.createElement("a");
    link.href = `#${heading.id}`;
    link.className = index === 0 ? "is-active" : "";
    link.innerHTML = `<span>${number.format(index + 1)}</span><strong>${escapeText(heading.textContent ?? `Section ${index + 1}`)}</strong>`;
    outline.append(link);
  });
}

function filterCollection(inputSelector: string, itemSelector: string): void {
  const input = document.querySelector<HTMLInputElement>(inputSelector);
  if (!input) return;
  input.addEventListener("input", () => {
    const query = input.value.trim().toLowerCase();
    document.querySelectorAll<HTMLElement>(itemSelector).forEach((item) => {
      const matches = !query || Boolean(item.dataset.searchable?.includes(query));
      item.hidden = !matches;
      if (query && matches && item instanceof HTMLDetailsElement) item.open = true;
    });
  });
}

function enhance(): void {
  document.querySelectorAll<HTMLAnchorElement>('a[href^="/"]').forEach((link) => {
    link.addEventListener("click", (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      navigate(link.getAttribute("href") ?? "/");
    });
  });
  document.querySelectorAll<HTMLElement>("[data-open-search]").forEach((button) => button.addEventListener("click", openSearch));
  document.querySelector<HTMLElement>("[data-close-search]")?.addEventListener("click", () => document.querySelector<HTMLDialogElement>("[data-search-dialog]")?.close());
  const globalSearch = document.querySelector<HTMLInputElement>("[data-global-search]");
  globalSearch?.addEventListener("input", () => {
    const results = document.querySelector<HTMLElement>("[data-search-results]");
    if (results) {
      results.innerHTML = searchResults(globalSearch.value);
      results.querySelectorAll<HTMLAnchorElement>('a[href^="/"]').forEach((link) => link.addEventListener("click", (event) => {
        event.preventDefault();
        document.querySelector<HTMLDialogElement>("[data-search-dialog]")?.close();
        navigate(link.getAttribute("href") ?? "/");
      }));
    }
  });
  const menuButton = document.querySelector<HTMLButtonElement>("[data-menu-trigger]");
  menuButton?.addEventListener("click", () => {
    const nav = document.querySelector<HTMLElement>("[data-mobile-nav]");
    if (!nav) return;
    nav.hidden = !nav.hidden;
    menuButton.setAttribute("aria-expanded", String(!nav.hidden));
  });
  document.querySelector<HTMLElement>("[data-open-sidebar]")?.addEventListener("click", (event) => {
    document.querySelector(".lesson-sidebar")?.classList.add("is-open");
    (event.currentTarget as HTMLElement).setAttribute("aria-expanded", "true");
  });
  document.querySelector<HTMLElement>("[data-close-sidebar]")?.addEventListener("click", () => {
    document.querySelector(".lesson-sidebar")?.classList.remove("is-open");
    document.querySelector("[data-open-sidebar]")?.setAttribute("aria-expanded", "false");
  });
  document.querySelectorAll<HTMLButtonElement>("[data-toggle-lesson-rail]").forEach((button) => button.addEventListener("click", () => {
    const side = button.dataset.toggleLessonRail;
    if (side !== "left" && side !== "right") return;
    const open = button.getAttribute("aria-expanded") !== "true";
    const label = `${open ? "Hide" : "Show"} ${side === "left" ? "curriculum" : "lesson outline"}`;
    document.querySelector(".lesson-page")?.classList.toggle(`is-${side}-collapsed`, !open);
    button.setAttribute("aria-expanded", String(open));
    button.setAttribute("aria-label", label);
    button.title = label;
    saveLessonRail(side, open);
  }));
  document.querySelectorAll<HTMLElement>("[data-toggle-complete]").forEach((button) => button.addEventListener("click", (event) => {
      const slug = (event.currentTarget as HTMLElement).dataset.toggleComplete;
      if (!slug) return;
      if (completed.has(slug)) completed.delete(slug);
      else completed.add(slug);
      localStorage.setItem(storageKey, JSON.stringify([...completed]));
      route();
    }));
  document.querySelector<HTMLSelectElement>("[data-module-select]")?.addEventListener("change", (event) => {
    navigate((event.currentTarget as HTMLSelectElement).value);
  });
  document.querySelector<HTMLElement>("[data-reset-progress]")?.addEventListener("click", () => {
    if (!window.confirm("Reset completed lessons for this course?")) return;
    completed = new Set();
    localStorage.removeItem(storageKey);
    route();
  });
  filterCollection("[data-curriculum-filter]", "[data-syllabus-list] > [data-searchable]");
  filterCollection("[data-sidebar-filter]", "[data-sidebar-list] > [data-searchable]");
  filterCollection("[data-dashboard-filter]", "[data-dashboard-list] > [data-searchable]");
  enhanceLesson();
}

document.addEventListener("keydown", (event) => {
  const target = event.target as HTMLElement;
  if (event.key === "/" && !["INPUT", "TEXTAREA"].includes(target.tagName)) {
    event.preventDefault();
    openSearch();
  }
  if (["ArrowUp", "ArrowDown"].includes(event.key) && !["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) {
    const lessons = [...document.querySelectorAll<HTMLAnchorElement>(".dashboard-lesson-row")].filter((lesson) => lesson.offsetParent !== null);
    if (!lessons.length) return;
    event.preventDefault();
    const focused = lessons.indexOf(document.activeElement as HTMLAnchorElement);
    const current = lessons.findIndex((lesson) => lesson.classList.contains("is-current"));
    const start = focused >= 0 ? focused : Math.max(current, 0);
    lessons[Math.max(0, Math.min(lessons.length - 1, start + (event.key === "ArrowDown" ? 1 : -1)))]?.focus();
  }
});

window.addEventListener("popstate", route);
route();
